import * as React from "react";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Popover, Spin } from "antd";
import axios, { isAxiosError } from "axios";
import { logger } from "@/helpers/tslog";
import { clearAuthLocalStorageAndReload, getToken } from "@/utils/localstorage";
import { PEERS_FILTER } from "./CompanySearchField";

export interface PeersCatalogCompanyRow {
  symbol: string;
  companyName: string;
  exchange: string;
  ticker: string;
}

interface PeersCatalogResponse {
  filter: string;
  companies: PeersCatalogCompanyRow[];
}

async function fetchPeersCatalog(): Promise<PeersCatalogCompanyRow[]> {
  const apiUrl = process.env.API_URL as string;
  const token = await getToken();
  try {
    const { data } = await axios.get<PeersCatalogResponse>(`${apiUrl}/search/peers-catalog`, {
      params: { filter: PEERS_FILTER },
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      withCredentials: true,
    });
    return Array.isArray(data.companies) ? data.companies : [];
  } catch (e) {
    if (isAxiosError(e) && e.response?.status === 401) {
      logger.warn("PeersCatalogInfoButton: 401 from /search/peers-catalog — clearing session and reloading");
      clearAuthLocalStorageAndReload();
      return [];
    }
    throw e;
  }
}

const POPOVER_OVERLAY_STYLE: React.CSSProperties = { maxWidth: 360 };

export function PeersCatalogInfoButton() {
  const [open, setOpen] = React.useState(false);
  const [rows, setRows] = React.useState<PeersCatalogCompanyRow[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!open || rows !== null || error !== null) return;

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await fetchPeersCatalog();
        if (!cancelled) setRows(list);
      } catch (e) {
        logger.error("PeersCatalogInfoButton: catalog fetch failed", e);
        if (!cancelled) setError("Could not load the company list. Try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, rows, error]);

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setRows(null);
      setError(null);
      setLoading(false);
    }
  };

  const content = (
    <div className="max-w-[340px] text-sm text-gray-800">
      <p className="m-0 mb-2 leading-relaxed text-gray-700">
        Company search here is limited to the set we currently support for this feature — not the full
        market. The list is maintained on the server and will grow over time (for example as more peers are
        onboarded). Below are the names available right now for this search.
      </p>
      {loading ? (
        <div className="flex justify-center py-4">
          <Spin size="small" />
        </div>
      ) : error ? (
        <p className="m-0 text-red-600">{error}</p>
      ) : rows && rows.length > 0 ? (
        <ul className="m-0 max-h-[min(40vh,280px)] list-disc space-y-1 overflow-y-auto overscroll-contain pl-4 pr-1">
          {rows.map((c) => (
            <li key={c.symbol} className="leading-snug">
              <span className="font-medium text-gray-900">{c.companyName}</span>
              {c.ticker ? (
                <span className="text-gray-600">
                  {" "}
                  ({c.ticker}
                  {c.exchange ? `, ${c.exchange}` : ""})
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="m-0 text-gray-600">No companies returned for the current configuration.</p>
      )}
    </div>
  );

  return (
    <Popover content={content} trigger="click" open={open} onOpenChange={onOpenChange} overlayStyle={POPOVER_OVERLAY_STYLE}>
      <button
        type="button"
        className="inline-flex shrink-0 items-center justify-center rounded p-0.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
        aria-label="Which companies can I search? Opens a list of currently supported companies."
      >
        <InfoCircleOutlined className="text-lg" />
      </button>
    </Popover>
  );
}
